// components/SocialSignInButtons.js
//
// "Continue with Google" for sign-up, sign-in and the RSVP popup.
//
// ── Why Google's own button, not a Supabase redirect ───────────────────────
//
// Supabase's OAuth redirect sends people through its own callback address, so
// Google's account chooser said "continue to lkhydnlkvqivdyrpxkts.supabase.co"
// - a stranger's-looking domain at the exact moment someone decides whether to
// trust the page. Changing that needs Supabase's paid custom-domain add-on.
//
// Google Identity Services instead hands this page a signed ID token directly,
// and Supabase signs the person in from it (signInWithIdToken). Nothing is
// redirected through Supabase, so Google shows invits.co, the person never
// leaves the page they were on (an invitation mid-RSVP stays put), and the
// same script offers One Tap.
//
// Needs REACT_APP_GOOGLE_CLIENT_ID - a "Web application" OAuth client with
// https://invits.co in its Authorised JavaScript origins - and that client ID
// in the Google provider's "Client IDs" in Supabase.
//
// Renders nothing while Google is off (shared/authOptions.js) or unconfigured.
import React, { useEffect, useRef, useState } from 'react';
import supabase from '../services/supabase';
import { completeOAuthSignIn } from '../services/authCodes';
import { socialProviders } from '../shared/authOptions';
import { track } from '../services/track';
import { EV } from '../shared/analyticsEvents';
import '../styles/SocialSignInButtons.scss';

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GSI_SRC = 'https://accounts.google.com/gsi/client';

let gsiPromise = null;
function loadGsi() {
  if (window.google?.accounts?.id) return Promise.resolve(window.google);
  if (!gsiPromise) {
    gsiPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = GSI_SRC;
      s.async = true;
      s.defer = true;
      s.onload = () => resolve(window.google);
      s.onerror = () => { gsiPromise = null; reject(new Error('Google sign-in could not load')); };
      document.head.appendChild(s);
    });
  }
  return gsiPromise;
}

/** A random nonce, and its SHA-256 - Google gets the hash, Supabase checks the raw value. */
async function makeNonce() {
  const raw = [...crypto.getRandomValues(new Uint8Array(16))].map((b) => b.toString(16).padStart(2, '0')).join('');
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
  const hashed = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  return { raw, hashed };
}

/**
 * @param {Function} [props.onSignedIn]  called with { created, profile_complete }
 *   once the session exists; pages decide where to go (the RSVP popup stays put)
 */
/**
 * @param {'top'|'bottom'} [props.placement]  where on the page it sits: the
 *   "or" divider goes between it and the form, so above it at the bottom and
 *   below it at the top
 */
export default function SocialSignInButtons({ onSignedIn, placement = 'bottom' }) {
  const enabled = socialProviders('web').includes('google') && !!CLIENT_ID;
  const holder = useRef(null);
  const onSignedInRef = useRef(onSignedIn);
  onSignedInRef.current = onSignedIn;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const [google, nonce] = await Promise.all([loadGsi(), makeNonce()]);
        if (cancelled || !holder.current) return;

        google.accounts.id.initialize({
          client_id: CLIENT_ID,
          nonce: nonce.hashed,
          ux_mode: 'popup',
          context: 'signup',
          itp_support: true,
          callback: async ({ credential }) => {
            setBusy(true);
            setError(null);
            try {
              const { error: signInError } = await supabase.auth.signInWithIdToken({
                provider: 'google',
                token: credential,
                nonce: nonce.raw,
              });
              if (signInError) throw signInError;
              const result = await completeOAuthSignIn();
              track(result?.created ? EV.SIGNUP_COMPLETED : EV.LOGIN_COMPLETED, { method: 'google', source: 'web' });
              onSignedInRef.current?.(result);
            } catch (e) {
              setError(e.message || 'Google sign-in failed. Please try again.');
            } finally {
              setBusy(false);
            }
          },
        });

        google.accounts.id.renderButton(holder.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'center',
          width: Math.min(holder.current.offsetWidth || 360, 400),
        });
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();

    return () => { cancelled = true; };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div className={`social-signin social-signin--${placement}`}>
      {placement === 'bottom' && <div className="social-signin-divider"><span>or</span></div>}
      <div ref={holder} className="social-signin-google" aria-busy={busy} />
      {busy && <p className="social-signin-note">Signing you in…</p>}
      {error && <p className="error-text">{error}</p>}
      {placement === 'top' && <div className="social-signin-divider"><span>or</span></div>}
    </div>
  );
}
